import { EFieldOptionsType } from './field-options-type.enum';

export const MODULE_ROUTES = {
  HOME: 'Module_Home',
  AUTH: 'Module_Auth',
  FH_VITALS: 'Module_FHVitals',
  DR_OLIVE: 'Module_DrOlive',
  TRIAGE: 'Module_Triage',
  QUESTIONNAIRE: 'Module_Questionnaire',
  BIOIMPEDANCE: 'Module_Bioimpedance',
  FINISH: 'Module_Finish',
  BINAH: 'Module_Binah',
  TELEMEDICINE: 'Module_Telemedicine',
  TEMPERATURE: 'Module_Temperature',
  EYECARE: 'Module_Eyecare',
  TEM_SAUDE: 'Module_TemSaude',
  CONEXA: 'Module_Conexa',
  BIO_TELEMED: 'Module_BioTelemed',
  DOCTOR_ONE: 'Module_DoctorOne',
  SERVICE_MENU: 'Module_ServiceMenu',
  SKIN_CARE: 'Module_SkinCare',
  MERCER_MARSH: 'Module_MercerMarsh',
  TI_SAUDE: 'Module_TiSaude',
  INTRO_MODULES_USER: 'Module_IntroModulesUser',
  PAYMENT: 'Module_Payment',
  COPSOQ: 'Module_COPSOQ',
  ECG: 'Module_ECG',
  AUDIOMETRY: 'Module_Audiometry'
} as const;

// Rótulos pt-BR exibidos na Dashboard. O banco guarda alguns nomes em inglês e a rota é a chave técnica,
// então o nome exibido vem daqui. Rota fora do mapa mantém o nome vindo da API.
export const MODULE_DISPLAY_NAMES: Record<string, string> = {
  [MODULE_ROUTES.HOME]: 'Início',
  [MODULE_ROUTES.AUTH]: 'Autenticação',
  [MODULE_ROUTES.FH_VITALS]: 'Face Heart Vitals',
  [MODULE_ROUTES.DR_OLIVE]: 'Dr Olive',
  [MODULE_ROUTES.TRIAGE]: 'Triagem',
  [MODULE_ROUTES.QUESTIONNAIRE]: 'Questionário',
  [MODULE_ROUTES.BIOIMPEDANCE]: 'Bioimpedância',
  [MODULE_ROUTES.FINISH]: 'NPS - Pesquisa de Satisfação',
  [MODULE_ROUTES.BINAH]: 'Binah',
  [MODULE_ROUTES.TELEMEDICINE]: 'Telemedicina Fake',
  [MODULE_ROUTES.TEMPERATURE]: 'Temperatura',
  [MODULE_ROUTES.EYECARE]: 'Eyecare',
  [MODULE_ROUTES.TEM_SAUDE]: 'Tem Saúde',
  [MODULE_ROUTES.CONEXA]: 'Conexa',
  [MODULE_ROUTES.BIO_TELEMED]: 'Bio Telemed',
  [MODULE_ROUTES.DOCTOR_ONE]: 'Doctor One',
  [MODULE_ROUTES.SERVICE_MENU]: 'Menu de Serviços',
  [MODULE_ROUTES.SKIN_CARE]: 'SkinCare',
  [MODULE_ROUTES.MERCER_MARSH]: 'Mercer Marsh',
  [MODULE_ROUTES.TI_SAUDE]: 'Ti Saúde',
  [MODULE_ROUTES.INTRO_MODULES_USER]: 'Introdução dos Módulos',
  [MODULE_ROUTES.PAYMENT]: 'Pagamento',
  [MODULE_ROUTES.COPSOQ]: 'NR1 - COPSOQ',
  [MODULE_ROUTES.ECG]: 'Eletrocardiograma',
  [MODULE_ROUTES.AUDIOMETRY]: 'Audiometria'
};

export function getModuleDisplayName(module: { route: string; name: string }): string {
  return MODULE_DISPLAY_NAMES[module.route] ?? module.name;
}

export const MODULE_DEPENDENCIES: Record<string, string[]> = {
  [MODULE_ROUTES.FH_VITALS]: [MODULE_ROUTES.TRIAGE],
  [MODULE_ROUTES.DR_OLIVE]: [MODULE_ROUTES.TRIAGE]
};

export function getModulesDependingOn(route: string): string[] {
  return Object.entries(MODULE_DEPENDENCIES)
    .filter(([, deps]) => deps.includes(route))
    .map(([dependentRoute]) => dependentRoute);
}

export function getMissingDependencies(moduleRoute: string, presentRoutes: string[]): string[] {
  const required = MODULE_DEPENDENCIES[moduleRoute];
  if (!required) return [];
  return required.filter((dep) => !presentRoutes.includes(dep));
}

// Rotas escolhidas nos campos do tipo "modules" da configuração (ex.: rotas do Intro Modules User).
// Esses módulos precisam estar também no fluxo do setup.
export function getSelectedModuleRoutes(
  configSchema: Record<string, { optionsType?: string } | null> | undefined,
  config: Record<string, unknown>
): string[] {
  return Object.entries(configSchema ?? {})
    .filter(([, field]) => field?.optionsType === EFieldOptionsType.Modules)
    .flatMap(([key]) => (Array.isArray(config[key]) ? (config[key] as string[]) : []));
}

// Rotas que estavam escolhidas na configuração anterior e foram desmarcadas na nova. Esses módulos
// saem do fluxo do setup.
export function getUnselectedModuleRoutes(
  configSchema: Record<string, { optionsType?: string } | null> | undefined,
  previousConfig: Record<string, unknown> | null | undefined,
  newConfig: Record<string, unknown>
): string[] {
  const selected = getSelectedModuleRoutes(configSchema, newConfig);
  return getSelectedModuleRoutes(configSchema, previousConfig ?? {}).filter((route) => !selected.includes(route));
}

// Módulos que o fluxo precisa ter quando algum módulo dele requer autenticação ou triagem. A
// Triagem vem antes porque ela mesma pode requerer autenticação.
export const REQUIRED_FLOW_MODULES = [
  { route: MODULE_ROUTES.TRIAGE, flag: 'requiresTriage' },
  { route: MODULE_ROUTES.AUTH, flag: 'requiresAuth' }
] as const;

// Indica se algum módulo do fluxo requer o Auth ou a Triagem. O Intro Modules User requer
// autenticação, mas não puxa o Auth pelo próprio flag: quem puxa são os módulos escolhidos nele.
export function isRequiredByFlow(
  modules: { route: string; requiresAuth: boolean; requiresTriage: boolean }[],
  flag: (typeof REQUIRED_FLOW_MODULES)[number]['flag']
): boolean {
  return modules.some((m) => m[flag] && m.route !== MODULE_ROUTES.INTRO_MODULES_USER);
}

// Posição em que o Auth e a Triagem entram no fluxo: o Auth logo depois da Home (ou no início, sem
// Home) e a Triagem logo depois do Auth.
export function getRequiredModuleIndex(route: string, flowRoutes: string[]): number {
  const anchor =
    route === MODULE_ROUTES.TRIAGE && flowRoutes.includes(MODULE_ROUTES.AUTH) ? MODULE_ROUTES.AUTH : MODULE_ROUTES.HOME;
  return flowRoutes.indexOf(anchor) + 1;
}
